import { supabase } from './supabase';

interface TeamData {
  id: string | null;
  check_in_times: string[] | null;
  sprints: {
    id: string;
    created_at: string;
  }[];
  accessibleTeams: {
    id: string;
    name: string;
  }[];
  role: string;
  userType: string;
  shadow_id: string | null;
  shadow_to: string | null;
  employeeId: string | null;
  isTeamMember: boolean;
}

export async function fetchUserTeam(
  userId?: string | null,
): Promise<TeamData | null> {
  if (!userId) {
    return null;
  }

  const { data: teamData, error: teamError } = await supabase
    .from('teams')
    .select('id,name')
    .or(
      `account_manager_id.eq.${userId},manager_id.eq.${userId},team_members.cs.${JSON.stringify(
        [userId],
      )}`,
    );

  const { data: userTeam, error: userTeamError } = await supabase
    .from('teams')
    .select(
      `
      id,
      check_in_times,
      sprints!inner (
        id,
        created_at
      )
    `,
    )
    .filter('team_members', 'cs', [`"${userId}"`])
    .eq('sprints.status', 'active')
    .order('created_at', { foreignTable: 'sprints', ascending: false })
    .limit(1)
    .maybeSingle();

  const { data: RoleAndType, error: RoleAndTypeError } = await supabase
    .from('users')
    .select('role,type,shadow_id,shadow_to,employee_id')
    .eq('id', userId)
    .maybeSingle();

  // Handle errors except for "no rows returned" (PGRST116)
  if (
    teamError ||
    RoleAndTypeError ||
    (userTeamError && userTeamError.code !== 'PGRST116')
  ) {
    throw new Error(
      `Failed to fetch team data: ${
        teamError?.message ||
        userTeamError?.message ||
        RoleAndTypeError?.message ||
        'Unknown error'
      }`,
    );
  }

  return {
    id: userTeam?.id as string,
    check_in_times: userTeam?.check_in_times as string[],
    sprints: userTeam?.sprints as { id: string; created_at: string }[],
    accessibleTeams: teamData as { id: string; name: string }[],
    role: RoleAndType?.role as string,
    userType: RoleAndType?.type as string,
    shadow_id: RoleAndType?.shadow_id as string | null,
    shadow_to: RoleAndType?.shadow_to as string | null,
    isTeamMember: !!userTeam?.id,
    employeeId: RoleAndType?.employee_id as string | null,
  };
}
